<template>
  <div class="jersey-numbers-height">
    <div class="jersey-numbers-height__sizes">
      <div
        v-for="size in sizes"
        :key="size.value"
        :class="{
          'jersey-numbers-height__size': true,
          'jersey-numbers-height__size--selected': isSelected(size)
        }"
        data-testid="transfers-number-height-item"
        @click="handleSelect(size)"
      >
        {{ size.label }}
      </div>

      <div
        :class="{
          'jersey-numbers-height__size': true,
          'jersey-numbers-height__size--selected': showCustomSize
        }"
        data-testid="transfers-number-height-item"
        @click="handleCustomSelect()"
      >
        Custom size
      </div>
    </div>

    <template v-if="showCustomSize">
      <p class="jersey-numbers-height__label">Height (inches)</p>
      <input
        v-model="customHeight"
        maxlength="4"
        type="tel"
        class="jersey-numbers-height__input"
        data-testid="transfers-number-custom-height-input"
        @input="handleCustomInputChange($event, updateHeight)"
      />
      <ArtworkAlert v-if="customHeight && !isValidCustomSize" class="jersey-numbers-height__error">
        Please enter a valid height between {{ dtfMinHeight }} and {{ dtfMaxHeight }} inches
      </ArtworkAlert>
    </template>
  </div>
</template>

<script setup>
import { computed, ref, watch, onMounted } from "vue";
import { calculateSizes } from "components/jiffy/vue-product-details-dtf/JerseyNumbers/utils";
import ArtworkAlert from "components/jiffy/vue-product-details-dtf/ArtworkAlert.vue";
import { useDtfStore } from "../store";

const customHeight = ref(null);
const showCustomSize = ref(false);
let timer = null;

const sizes = [
  { label: "4 Inches", value: 4 },
  { label: "6 Inches", value: 6 },
  { label: "8 Inches", value: 8 },
  { label: "10 Inches", value: 10 }
];

const store = useDtfStore();
const letterNumberArtwork = computed(() => store.letterNumberArtwork);
const isSelected = size => letterNumberArtwork.value.height === size.value && !showCustomSize.value;
const loadFromConfig = computed(() => store.loadFromConfig);
const dtfMinHeight = computed(() => store.dtfMinHeight);
const dtfMaxHeight = computed(() => store.dtfMaxHeight);

const isValidCustomSize = computed(
  () =>
    Number(letterNumberArtwork.value.height) >= dtfMinHeight.value &&
    Number(letterNumberArtwork.value.height) <= dtfMaxHeight.value
);

const updateHeight = height => {
  const artworkSizes = calculateSizes({ ...letterNumberArtwork.value, height });
  const isValid = isValidCustomSize;
  store.updateArtworkValues({ index: 0, isValid, height, sizes: artworkSizes });
  if (isValid.value && Object.keys(artworkSizes).length) {
    store.getArtworksPricing();
  }

  store.updateJerseyNumberPdpDates();
};

const handleSelect = size => {
  showCustomSize.value = false;
  customHeight.value = null;
  const height = size.value;
  updateHeight(height);
};

const handleCustomSelect = () => {
  // disable 'Add to cart' button until user enters a valid height
  store.updateArtworkValues({ index: 0, isValid: false });

  showCustomSize.value = true;
};

const handleCustomInputChange = (event, callback) => {
  clearTimeout(timer);

  const { value } = event.target;
  let inputValue = value.match(/(\d+(\.\d*)?)/);

  // Any null matching should default to 0
  inputValue = inputValue ? inputValue[0] : "0";

  const splitHeight = inputValue.split(".");

  // Skip formatting to 2 decimal places if last element is '.'
  if (splitHeight[1] && splitHeight[1].length > 2) {
    // Format inputValue to 2 decimal places
    // We can't use `toFixed` since its rounding the digits off
    inputValue = Number([splitHeight[0], splitHeight[1].slice(0, 2)].join("."));
  }
  event.target.value = inputValue;

  // Delay setting of inputValue once user stops typing for 1 second
  timer = setTimeout(() => {
    callback(inputValue);
  }, 1000);
};

watch(
  () => letterNumberArtwork.value.height,
  (newValue, oldValue) => {
    // clearWorkspace was called
    if (oldValue && newValue === null) {
      customHeight.value = null;
      showCustomSize.value = false;
    }
  }
);

onMounted(() => {
  const presetHeight = sizes.find(size => size.value === letterNumberArtwork.value.height);
  if (!loadFromConfig.value && !presetHeight) {
    showCustomSize.value = true;
    customHeight.value = letterNumberArtwork.value.height;
  }
  const isValid = isValidCustomSize;
  store.updateArtworkValues({ index: 0, isValid });
});
</script>

<style lang="scss">
@import "frontend/init/jiffy/settings.scss";
@import "frontend/init/jiffy/mixin.scss";

.jersey-numbers-height {
  &__sizes {
    display: flex;
    gap: 12px;
    flex-wrap: wrap;
  }

  &__size {
    padding: 12px 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: $text-black;
    font-size: 13px;
    font-weight: 700;
    line-height: 1.3;
    cursor: pointer;
    border-radius: 4px;
    border: 2px solid $gray-border;
    background: $white;

    &--selected {
      border-color: $primary-600;
    }
  }

  &__label {
    margin: 24px 0 12px;
    color: $text-black;
    font-size: 16px;
    font-weight: 400;
    line-height: 1.1;
  }

  &__input {
    appearance: none;
    border-radius: 4px;
    border: 1px solid $gray-label;
    background: $white;
    padding: 13px 18px;
    color: $text-black;
    text-align: center;
    font-size: 14px;
    font-weight: 400;
    line-height: 1.42;
  }

  &__error {
    margin-top: 8px;
  }
}
</style>
